import { createElement, useEffect, useState, useTransition } from "react";

import { useTranslations } from "next-intl";

import { buildSingleNodeFromMultiSuggest } from "@/components/expression/preview/ExpressionNodePreview";
import { insertGraph } from "@/components/graph/insertGraph";
import { pyodideManager } from "@/components/pyodide/PyodideManager";
import { insertNodeFromSuggestion } from "@/components/suggestions/utils";
import { executeComputedCommand } from "@/components/suggestions/utils/computedCommands";
import { insertedNodeType, libraryRootNode } from "@/components/suggestions/utils/libraryItem";
import { filterMenuGroups } from "@/components/suggestions/utils/menuSearch";
import { MathColorPopover } from "@/components/tiptap/color/MathColorPopover";
import { executeClipboardCommand } from "@/components/tiptap/copy-paste/clipboardCommands";
import { openSelectionPrompt } from "@/components/tiptap/selection-prompt/openSelectionPrompt";
import { editNode, insertNode } from "@/input/utils/editOperators";
import {
  applyGroupSymbols,
  closeOpenGroupLeft,
  closeOpenGroupRight,
  selectedFunctionNode,
  setFunctionParenthesesVisibility,
  shouldApplyGroupSymbols,
} from "@/input/utils/openGroup";
import { demotePower, movePowerValueToLabel } from "@/input/utils/powerValue";
import { runSympyAction } from "@/input/utils/sympyAction";
import { mergeGroupIntoVariable, splitVariableArguments } from "@/input/utils/variableArguments";
import { cursorIsActive } from "@/models/CursorModel";
import { FunctionNodeModel } from "@/models/nodes/FunctionNodeModel";
import { GroupNodeModel } from "@/models/nodes/GroupNodeModel";
import { InlineNodeModel } from "@/models/nodes/InlineNodeModel";
import { nodeFromJson } from "@/models/nodes/nodeFromJson";
import { OperatorNodeModel } from "@/models/nodes/OperatorNodeModel";
import { PlaceholderNodeModel } from "@/models/nodes/PlaceholderNodeModel";
import { VariableNodeModel } from "@/models/nodes/VariableNodeModel";
import { trackEvent, trackEventOncePerSession } from "@/utils/trackEvent";
import { trackNodeTypeCreated, trackNodesCreated } from "@/utils/trackNodeCreation";

import { insertColumn, isMatrixCell } from "../expression/utils/matrix";

import { getDefaultAccept } from "./providers";
import { getSuggestionNode } from "./providers/utils";
import { escapeUnitForDisallowedInput, isAllowedInUnit } from "./utils/isAllowedInUnit";

import type {
  GroupSuggestionProps,
  ProviderSearchOptions,
  Suggestion,
  SuggestionItem,
  SuggestionNodeType,
  SuggestionProviderType,
  SuggestionsGroup,
} from "@/components/suggestions/types";
import type { DocumentModel } from "@/models/DocumentModel";
import type { NodeModel } from "@/models/nodes/types";
import type { NodeJson, SuggestionAction } from "@corca/data";

/**
 * Hook that accepts a search query and returns a filtered list of suggestions
 *
 * @param search The search query to filter suggestions.
 * @param doc Document
 * @param accept Array of suggestion provider types to include. Includes all providers by default
 * @returns An array of options filtered based on the search query.
 */
type UseSuggestionsOptions = {
  providerSearchOptions?: ProviderSearchOptions;
  flat?: boolean;
  initialIndex?: number;
};

export function useSuggestions<T extends SuggestionNodeType>(
  search: string,
  doc: DocumentModel,
  accept?: SuggestionProviderType[],
  options?: UseSuggestionsOptions,
): { isPending: boolean; listGroups: SuggestionsGroup[] } {
  const t = useTranslations("suggestions");
  const { from, to } = doc.editor.textSelection;
  const isRange = from !== to;
  const { providerSearchOptions, flat, initialIndex } = options ?? {};
  const { selectedNodes } = doc.selection;
  const { pointerNode } = doc.editor.suggestions;

  const [suggestions, setSuggestions] = useState<SuggestionsGroup[]>([]);

  const [isPending, startTransition] = useTransition();

  const isPyodideReady = pyodideManager.isReady;

  useEffect(() => {
    doc.editor.suggestions.collectSuggestions<T>({
      doc,
      search,
      accept: accept ?? getDefaultAccept(),
      options: {
        providerSearchOptions,
        flat,
        initialIndex,
      },
    });
  }, [
    isPyodideReady,
    search,
    doc,
    selectedNodes,
    pointerNode,
    accept,
    isRange,
    providerSearchOptions,
    flat,
    initialIndex,
    doc.editor.editorState.latexInput,
    doc.libraries.enabled,
    doc.libraries.status,
  ]);

  const { query, unfilteredGroups } = doc.editor.suggestions;

  useEffect(() => {
    doc.editor.suggestions.setFilteredGroups(filterMenuGroups(unfilteredGroups, query, t));
  }, [doc, unfilteredGroups, query, t]);

  useEffect(() => {
    startTransition(() => {
      setSuggestions(doc.editor.suggestions.currentListGroups);
    });
  }, [doc.editor.suggestions.currentListGroups]);

  return { isPending, listGroups: suggestions };
}

export const handlePickSuggestion = (
  doc: DocumentModel,
  suggestion: Suggestion,
  options?: {
    selectedNode?: NodeModel;
    preserveHints?: boolean;
    isSelectedManually?: boolean;
    skipCursorMove?: boolean;
  },
): NodeModel[] | null => {
  const {
    selectedNode,
    preserveHints = false,
    isSelectedManually = false,
    skipCursorMove = false,
  } = options ?? {};
  const isEditing = doc.input.isEditing(selectedNode);

  if (suggestion.type === "editNode") {
    const { pointerNode } = doc.editor.suggestions;
    if (pointerNode?.isEditable) {
      doc.selection.resetSelection();
      doc.input.editNode(pointerNode);
      doc.cursor.moveBeforeNode(pointerNode);
    } else if (doc.selection.hasSelectedEditableNode) {
      doc.selection.editSelectedNode();
    }
    return null;
  }

  if (suggestion.type === "clipboardCommand") {
    executeClipboardCommand(suggestion.command, doc);
    doc.editor.suggestions.reset();
    return null;
  }

  if (suggestion.type === "computedCommand") {
    executeComputedCommand(suggestion.command, doc);
    doc.editor.suggestions.reset();
    return null;
  }

  if (suggestion.type === "collapseBlock") {
    doc.editor.tiptap?.commands.setCollapsed(suggestion.positions, !suggestion.collapsed);
    doc.editor.suggestions.reset();
    return null;
  }

  if (suggestion.type === "color") {
    if (suggestion.target === "node") {
      const { nodeId } = suggestion;

      doc.editor.suggestions.reset();
      doc.editor.tiptap?.commands.openPopover((close) =>
        createElement(MathColorPopover, { doc, nodeId, close }),
      );
    }

    return null;
  }

  if (suggestion.type === "selectionPrompt") {
    openSelectionPrompt(doc, "suggestions");
    return null;
  }

  if (suggestion.type === "single" && suggestion.item.isSymbol) {
    doc.input.setValue(suggestion.item.symbol);
    return null;
  }

  if (suggestion.type === "mention") {
    if (!cursorIsActive(doc.cursor)) {
      doc.api.rememberMentionedUser(suggestion.user);

      doc.transact(() => {
        doc.editor.tiptap
          ?.chain()
          .appendMention(doc.input.inputPosRange, {
            userId: suggestion.user.uid,
            username: suggestion.user.username,
            withTrailingSpace: true,
          })
          .focus()
          .run();
      }, "TiptapSuggestions:insertMention");

      doc.input.reset();
    }
    return null;
  }

  const { cursor } = doc;

  if (!cursorIsActive(cursor)) {
    return null;
  }

  if (suggestion.type === "multi") {
    return doc.transact(
      () => {
        const { parent } = cursor.node;
        let placeholder: PlaceholderNodeModel | undefined;

        if (parent?.is(PlaceholderNodeModel)) {
          placeholder = parent;

          doc.cursor.moveBeforeNode(parent);
          doc.removeNode(parent);
        }

        const inlineNode = cursor.node;
        const { index: newIndex, nodes } = buildSingleNodeFromMultiSuggest(
          suggestion,
          doc,
          inlineNode,
          cursor.index,
        );
        const moveTo = cursor.node.nodes[newIndex];

        if (placeholder) {
          doc.placeholderRegistry.replacePlaceholders(placeholder, nodes);
        }

        const lastNode = inlineNode.nodes[newIndex - 1];
        const emptyParams =
          lastNode?.is(FunctionNodeModel) && lastNode.parameters[0]?.isEmpty
            ? lastNode.parameters[0]
            : undefined;

        if (emptyParams) {
          cursor.moveTo(emptyParams, 0);
        } else if (moveTo) {
          cursor.moveBeforeNode(cursor.node.nodes[newIndex]);
        } else {
          cursor.moveTo(inlineNode, newIndex);
        }

        doc.editor.resetInput();

        // Track multinode usage (fires every time, regardless of node types)
        const docId = doc.id;
        trackEvent("nodes_created_from_multinode", { doc_id: docId });

        // Track each item in the multi-node suggestion (fires only once per session per type)
        // Since buildSingleNodeFromMultiSuggest doesn't return the created nodes,
        // we track based on the suggestion items instead
        suggestion.items.forEach((item) => {
          trackNodeTypeCreated(insertedNodeType(item), "multinode", {
            name: item.displayName,
            docId,
          });
        });

        return nodes;
      },
      "suggestion",
      { forceNewUndoStack: true },
    );
  }

  const isEqualsOperatorSymbol =
    suggestion.type === "single" && suggestion.item.key === "symbol-equal";

  return doc.transact(
    () => {
      if (suggestion.type === "single" && suggestion.item.type === "library") {
        const { item } = suggestion;
        const replacesEditedNode = isEditing && !!selectedNode && !!libraryRootNode(item);

        if (!replacesEditedNode) {
          let inserted: NodeModel[] | null = null;
          const { nodesJson = [], targetNode, fallbackTargetNode } = item;

          const insertLibraryNodes = () => {
            escapeUnitForDisallowedInput(cursor, isAllowedInUnit(suggestion, cursor.node));
            inserted = insertExpression(doc, { nodesJson, targetNode, fallbackTargetNode }, item, {
              isSelectedManually,
              skipCursorMove,
            });
            if (inserted) doc.editor.resetInput();
          };

          if (preserveHints) {
            doc.editor.hints.preservePhantomHint(insertLibraryNodes);
          } else {
            insertLibraryNodes();
          }

          if (inserted) {
            if (!isEqualsOperatorSymbol) {
              doc.editor.hints.setPhantomHints([]);
            }
            return inserted;
          }
        }
      } else if (suggestion.action) {
        const actionResult = applyAction(doc, suggestion.action, suggestion.item, {
          isSelectedManually,
          skipCursorMove,
        });
        if (actionResult) {
          doc.editor.resetInput();
          return null;
        }
      }

      const suggestionNode = getSuggestionNode(suggestion, doc) ?? [];
      const [newNode] = suggestionNode;

      const insertSuggestionNode = () => {
        let newCursor = suggestionNode[1];
        let cursorMoved = false;

        if (newNode) {
          if (isEditing && selectedNode) {
            const [, updatedCursor] = editNode(selectedNode, newNode);

            newCursor = updatedCursor;

            // Always move cursor to the end of the edited node if it doesn't have parameters
            if (newCursor === false) {
              newCursor = undefined;
            }
          } else {
            escapeUnitForDisallowedInput(cursor, isAllowedInUnit(suggestion, cursor.node));
            cursorMoved = insertNodeFromSuggestion(
              cursor,
              newNode,
              suggestion,
              skipCursorMove,
            ).cursorMoved;
          }

          if (!skipCursorMove && !cursorMoved) {
            if (newCursor) {
              doc.cursor.moveTo(newCursor.node, newCursor.index);
            } else if (newCursor !== false) {
              doc.cursor.moveAfterNode(newNode);
            }
          }
        }

        // newNode was not added to the document after the suggestion was selected. Removing the orphan node.
        if (newNode && !newNode.parent) {
          cursor.doc.removeNode(newNode);
        }

        doc.editor.resetInput();
      };

      if (preserveHints) {
        doc.editor.hints.preservePhantomHint(insertSuggestionNode);
      } else {
        insertSuggestionNode();
      }

      if (!isEqualsOperatorSymbol) {
        doc.editor.hints.setPhantomHints([]);
      }

      if (newNode) {
        const source = isSelectedManually ? "suggest" : "continuous typing";
        trackNodeTypeCreated(insertedNodeType(suggestion.item), source, {
          name: suggestion.item.displayName,
          docId: doc.id,
        });
      }

      return newNode ? [newNode] : null;
    },
    "suggestion",
    { forceNewUndoStack: true },
  );
};

function applyAction(
  doc: DocumentModel,
  suggestionAction: SuggestionAction,
  item: SuggestionItem,
  {
    isSelectedManually,
    skipCursorMove = false,
  }: {
    isSelectedManually?: boolean;
    skipCursorMove?: boolean;
  } = {},
) {
  switch (suggestionAction.type) {
    case "open-group":
      if (item.props && "openingSymbol" in item.props && item.props.openingSymbol?.symbol) {
        const { symbol } = item.props.openingSymbol;

        return closeOpenGroupLeft(doc, symbol);
      }
      break;

    case "close-group":
      if (item.props && "closingSymbol" in item.props && item.props.closingSymbol?.symbol) {
        const { symbol } = item.props.closingSymbol;

        return closeOpenGroupRight(doc, symbol);
      }
      break;

    case "set-function-group-symbols": {
      const functionNode = shouldApplyGroupSymbols(doc) ?? selectedFunctionNode(doc);

      if (functionNode && item.props) {
        const { openingSymbol, closingSymbol } = item.props as GroupSuggestionProps;

        applyGroupSymbols(functionNode, openingSymbol?.symbol, closingSymbol?.symbol);
        return true;
      }

      break;
    }

    case "demote-power":
    case "move-power-value-to-label": {
      const power = doc.nodes.get(suggestionAction.powerId);

      if (power?.is(OperatorNodeModel)) {
        if (suggestionAction.type === "demote-power") {
          demotePower(power, suggestionAction.count);
        } else {
          movePowerValueToLabel(power, suggestionAction.count);
        }

        return true;
      }

      break;
    }

    case "split-arguments": {
      const node = doc.nodes.get(suggestionAction.nodeId);

      if (node?.is(VariableNodeModel)) {
        splitVariableArguments(node);
        return true;
      }

      break;
    }

    case "merge-group-into-variable": {
      const node = doc.nodes.get(suggestionAction.nodeId);
      const group = doc.nodes.get(suggestionAction.groupId);

      if (node?.is(VariableNodeModel) && group?.is(GroupNodeModel)) {
        mergeGroupIntoVariable(node, group);
        return true;
      }

      break;
    }

    case "insert-graph": {
      insertGraph(doc, suggestionAction.expressions);
      return true;
    }

    case "set-function-parentheses-visibility": {
      const functionNode = selectedFunctionNode(doc);

      if (functionNode) {
        setFunctionParenthesesVisibility(functionNode, suggestionAction.visibility);
        return true;
      }

      break;
    }

    case "apply-function": {
      const { nodeBeforeCursor: node } = doc.cursor;
      if (node?.is(VariableNodeModel)) {
        if (item.props && "openingSymbol" in item.props) {
          node.openingSymbol = item.props.openingSymbol?.symbol;
        }
        if (item.props && "closingSymbol" in item.props) {
          node.closingSymbol = item.props.closingSymbol?.symbol;
        }
        const inline = doc.create(InlineNodeModel);
        node?.appendChild(inline);

        if (!skipCursorMove) {
          doc.cursor.moveTo(inline, 0);
        }

        return true;
      }
      break;
    }

    case "replace-nodes": {
      let parent: NodeModel | undefined = doc.cursor.node;
      let index = 0;

      for (const id of suggestionAction.ids) {
        const n = doc.nodes.get(id);
        if (n) {
          parent = n.parent;
          index = n.index;
          doc.removeNode(n);
        }
      }

      if (!skipCursorMove) {
        doc.cursor.moveTo(parent, index);
      }

      break;
    }

    case "add-augmented-bar": {
      const { node } = doc.cursor;

      if (isMatrixCell(node)) {
        const { parent: matrixNode } = node;
        const position = matrixNode.getCellPosition(doc.cursor.node);

        if (position) {
          if (position.col === matrixNode.cols - 1) {
            insertColumn(node);
          }

          matrixNode.addAugmentedBar(position.col);

          return true;
        }
      }

      break;
    }

    case "insert-expression":
      return !!insertExpression(doc, suggestionAction, item, {
        isSelectedManually,
        skipCursorMove,
      });

    case "sympy-action":
      runSympyAction(doc, suggestionAction.command).catch((err) => {
        console.error(err);
      });
      return true;

    case "apply-decorator":
    case "remove-labels":
      break;
  }
  return false;
}

type InsertExpression = Omit<Extract<SuggestionAction, { type: "insert-expression" }>, "type">;

/**
 * Pastes an expression's nodes at the cursor and moves the cursor to its target node. Returns
 * `true` once the nodes are inserted.
 */
function insertExpression(
  doc: DocumentModel,
  action: InsertExpression,
  item: SuggestionItem,
  {
    isSelectedManually,
    skipCursorMove = false,
  }: {
    isSelectedManually?: boolean;
    skipCursorMove?: boolean;
  } = {},
): NodeModel[] | null {
  const {
    nodesJson,
    targetNode: prioritizedTargetNode,
    fallbackTargetNode,
    deleteSelection,
  } = action;
  const targetNode = prioritizedTargetNode ?? fallbackTargetNode;

  if (cursorIsActive(doc.cursor) && nodesJson.length > 0) {
    const nodes = doc.batchCreateNodes(() => {
      return nodesJson.map((sn) => nodeFromJson(doc, sn, { resetIds: true })).filter(Boolean);
    });

    const { selectedNodes } = doc.selection;
    const [anchor] = deleteSelection ? selectedNodes : [];
    const { node: cursorNode, index: cursorIndex } = doc.cursor;

    for (const node of nodes) {
      if (anchor) {
        anchor.insertSiblingBefore(node);
      } else {
        insertNode(doc.cursor, node);
      }

      doc.cursor.moveAfterNode(node);
    }

    if (deleteSelection) {
      selectedNodes.forEach((node) => doc.removeNode(node));
    }

    if (skipCursorMove && doc.cursor.node === cursorNode) {
      doc.cursor.moveTo(cursorNode, cursorIndex);
    }

    if (targetNode) {
      if (!targetNode.path) {
        if (!skipCursorMove) {
          doc.cursor.moveBeforeNode(nodes[targetNode.index]);
        }

        // Track expression and individual nodes
        trackExpressionCreated(item, nodesJson, doc.id, isSelectedManually);
        return nodes;
      }

      const pathSegments = targetNode.path.split("::");
      let currentNode: NodeModel | undefined = undefined;

      for (const pathSegment of pathSegments) {
        const [type, indexStr] = pathSegment.split(":");
        const nodeIndex = parseInt(indexStr, 10);
        const targetArray: NodeModel[] = currentNode?.childrenPlease ?? nodes;
        const candidate = targetArray[nodeIndex];

        if (!candidate || candidate.type !== type) {
          return null;
        }

        currentNode = candidate;
      }

      if (!skipCursorMove && currentNode) {
        doc.cursor.moveTo(currentNode, targetNode.index);
      }
    }

    // Track expression and individual nodes
    trackExpressionCreated(item, nodesJson, doc.id, isSelectedManually);
    return nodes;
  }

  return null;
}

function trackExpressionCreated(
  item: SuggestionItem,
  nodesJson: NodeJson[],
  docId: string | undefined,
  isSelectedManually?: boolean,
) {
  const source = isSelectedManually ? "suggest" : "continuous typing";

  trackEventOncePerSession(
    "expression_created",
    { source, name: item.displayName, doc_id: docId },
    item.displayName,
  );
  trackNodesCreated(nodesJson, item.type === "library" ? "library" : "expression");
}
